import React, { useState } from 'react';
import {
  Modal,
  Table,
  Input,
  DatePicker,
  Select,
  Button,
  message,
  Tag,
  Space,
  Tooltip,
} from 'antd';
import {
  ShoppingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { WorkOrderLinePart } from '../../types';
import api from '../../services/api';
import dayjs from 'dayjs';

interface PartsToOrderModalProps {
  visible: boolean;
  onClose: () => void;
  workOrderId: number;
  parts: WorkOrderLinePart[];
  onUpdate: () => void;
}

const PartsToOrderModal: React.FC<PartsToOrderModalProps> = ({
  visible,
  onClose,
  workOrderId,
  parts,
  onUpdate,
}) => {
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<WorkOrderLinePart>>({});
  const [loading, setLoading] = useState(false);

  const handleEdit = (part: WorkOrderLinePart) => {
    setEditingKey(part.id!);
    setEditForm({
      supplier: part.supplier,
      tracking_number: part.tracking_number,
      expected_delivery: part.expected_delivery,
      status: part.status,
      notes: part.notes,
    });
  };

  const handleSave = async (part: WorkOrderLinePart) => {
    if (!part.id || !part.line_item_id) return;

    try {
      setLoading(true);

      // First, fetch the current line item to get all its parts
      const lineItemResponse = await api.get(`/work-orders/${workOrderId}/line-items/${part.line_item_id}`);
      const lineItem = lineItemResponse.data;

      // Update only the specific part in the parts array
      const updatedParts = (lineItem.parts || []).map((p: WorkOrderLinePart) => {
        const isEditedPart = p.id === part.id;
        const source = isEditedPart ? { ...p, ...editForm } : p;

        // Build the part object with only the fields the backend expects
        return {
          part_name: source.part_name,
          part_number: source.part_number || undefined,
          quantity: source.quantity,
          unit_price: source.unit_price,
          total_price: source.total_price,
          status: source.status,
          supplier: source.supplier || undefined,
          tracking_number: source.tracking_number || undefined,
          expected_delivery: source.expected_delivery || undefined,
          notes: source.notes || undefined,
        };
      });

      // Update the line item with the modified parts array
      await api.put(`/work-orders/${workOrderId}/line-items/${part.line_item_id}`, {
        parts: updatedParts
      });

      message.success('Part updated successfully');
      setEditingKey(null);
      setEditForm({});
      onUpdate();
    } catch (error) {
      message.error('Failed to update part');
      console.error('Error updating part:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditForm({});
  };

  const columns = [
    {
      title: 'Part',
      dataIndex: 'part_name',
      key: 'part_name',
      width: 200,
      render: (text: string, record: WorkOrderLinePart) => (
        <div>
          <div className="font-medium">{text}</div>
          {record.part_number && (
            <div className="text-xs text-gray-500">PN: {record.part_number}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: WorkOrderLinePart['status'], record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <Select
              value={editForm.status || status}
              onChange={(value: 'needed' | 'ordered' | 'received' | 'installed') => setEditForm({ ...editForm, status: value })}
              style={{ width: '100%' }}
              size="small"
            >
              <Select.Option value="needed">Needed</Select.Option>
              <Select.Option value="ordered">Ordered</Select.Option>
              <Select.Option value="received">Received</Select.Option>
              <Select.Option value="installed">Installed</Select.Option>
            </Select>
          );
        }
        return (
          <Tag
            color={
              status === 'installed' ? 'green' :
                status === 'received' ? 'blue' :
                  status === 'ordered' ? 'orange' :
                    'red'
            }
          >
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 150,
      render: (text: string, record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <Input
              value={editForm.supplier || ''}
              onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })}
              placeholder="Enter vendor"
              size="small"
            />
          );
        }
        return text || <span className="text-gray-400">-</span>;
      },
    },
    {
      title: 'Tracking #',
      dataIndex: 'tracking_number',
      key: 'tracking_number',
      width: 150,
      render: (text: string, record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <Input
              value={editForm.tracking_number || ''}
              onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })}
              placeholder="Enter tracking"
              size="small"
            />
          );
        }
        return text || <span className="text-gray-400">-</span>;
      },
    },
    {
      title: 'ETA',
      dataIndex: 'expected_delivery',
      key: 'expected_delivery',
      width: 150,
      render: (text: string, record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <DatePicker
              value={editForm.expected_delivery ? dayjs(editForm.expected_delivery) : null}
              onChange={(date) => setEditForm({ ...editForm, expected_delivery: date?.toISOString() })}
              size="small"
              style={{ width: '100%' }}
            />
          );
        }
        return text ? new Date(text).toLocaleDateString() : <span className="text-gray-400">-</span>;
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (text: string, record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <Input.TextArea
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              placeholder="Enter notes"
              size="small"
              rows={2}
            />
          );
        }
        return (
          <Tooltip title={text}>
            <div className="truncate">{text || <span className="text-gray-400">-</span>}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: WorkOrderLinePart) => {
        if (editingKey === record.id) {
          return (
            <Space size="small">
              <Button
                type="link"
                icon={<SaveOutlined />}
                onClick={() => handleSave(record)}
                loading={loading}
                size="small"
              />
              <Button
                type="link"
                icon={<CloseOutlined />}
                onClick={handleCancel}
                size="small"
              />
            </Space>
          );
        }
        return (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ShoppingOutlined style={{ fontSize: 18, color: '#f97316' }} />
          <span>Parts to Order ({parts.length})</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      centered
    >
      <div className="mb-4 p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
        <p className="text-sm text-gray-700 m-0">
          <strong>Note:</strong> These are parts that need to be ordered or are currently on order.
          Update the status, vendor, tracking number, and ETA as needed.
        </p>
      </div>

      <Table
        dataSource={parts}
        columns={columns}
        rowKey={(record) => String(record.id ?? record.part_number ?? record.part_name ?? 'new')}
        pagination={false}
        scroll={{ x: 1000 }}
        size="small"
      />
    </Modal>
  );
};

export default PartsToOrderModal;
